import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/design04/Header';
import {
    Coffee,
    Award,
    Heart,
    Shield,
    Star,
    ArrowLeft,
    CheckCircle,
    Thermometer,
    Droplets,
    Mountain,
    Leaf,
    Zap,
    Globe,
    ArrowRight,
    Target,
    Clock,
    Package,
    Users,
} from 'lucide-react';

const CinnamonPage = () => {
    return (
        <>
            <title>Ceylon Cinnamon - The True Cinnamon | Claude&apos;s Special</title>
            <meta
                name="description"
                content="Discover authentic Ceylon Cinnamon from Sri Lanka. Learn about the world's finest cinnamon variety, its health benefits, and why it's considered the gold standard of spices."
            />
            <meta
                name="keywords"
                content="Ceylon Cinnamon, true cinnamon, Cinnamomum verum, Sri Lankan cinnamon, health benefits, premium spices"
            />
            <meta property="og:title" content="Ceylon Cinnamon - The True Cinnamon" />
            <meta
                property="og:description"
                content="Authentic Ceylon Cinnamon from Sri Lanka - the world's finest cinnamon with unmatched quality and health benefits."
            />
            <meta property="og:type" content="article" />
            <link rel="canonical" href="/ceylon-cinnamon" />

            <Header />

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50">
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-orange-50/50 to-red-50/70"></div>
                    <div className="absolute inset-0 opacity-25">
                        <img
                            src="https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg"
                            alt="Ceylon Cinnamon bark being harvested"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    {/* Decorative Elements */}
                    <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-orange-400/30 to-red-400/30 rounded-full blur-2xl animate-pulse"></div>
                    <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-yellow-400/30 to-orange-400/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
                </div>

                <div className="relative z-10 text-center max-w-7xl mx-auto px-4 pt-24">
                    <div className="mb-8">
                        <Link
                            to="/"
                            className="inline-flex items-center space-x-3 bg-white/90 backdrop-blur-xl rounded-full px-6 py-3 mb-8 hover:bg-white transition-colors duration-200 border border-orange-200 shadow-lg"
                        >
                            <ArrowLeft className="h-5 w-5 text-orange-600" />
                            <span className="font-bold text-gray-800">Back to Home</span>
                        </Link>
                    </div>

                    <div className="inline-flex items-center space-x-3 bg-white/90 backdrop-blur-xl rounded-full px-8 py-4 mb-8 border border-orange-200 shadow-lg">
                        <Coffee className="h-6 w-6 text-orange-600" />
                        <span className="font-bold text-lg text-gray-800">Cinnamomum Verum</span>
                        <Star className="h-5 w-5 text-yellow-500" />
                    </div>

                    <h1 className="text-6xl md:text-8xl font-black mb-8 leading-tight">
                        <span className="bg-gradient-to-r from-orange-600 via-red-500 to-orange-600 bg-clip-text text-transparent">
                            Ceylon
                        </span>
                        <br />
                        <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 bg-clip-text text-transparent">
                            Cinnamon
                        </span>
                    </h1>

                    <p className="text-2xl md:text-3xl mb-8 font-medium text-orange-700">The World&apos;s Only True Cinnamon</p>

                    <p className="text-lg mb-16 text-gray-700 max-w-4xl mx-auto leading-relaxed">
                        Scientifically known as Cinnamomum verum, Ceylon Cinnamon is nature&apos;s most precious spice, cultivated
                        exclusively in Sri Lanka&apos;s mystical highlands for over 2,000 years.
                    </p>
                </div>
            </section>

            {/* What Makes It Special */}
            <section className="py-32 bg-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 rounded-full px-8 py-4 mb-8 border border-orange-200">
                            <Star className="h-6 w-6" />
                            <span className="font-bold text-lg">What Makes It Special</span>
                        </div>
                        <h2 className="text-5xl font-black text-gray-900 mb-8">
                            The Crown Jewel of
                            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent"> Spices</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                            Unlike common cassia cinnamon, Ceylon Cinnamon offers unparalleled quality, safety, and health benefits
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="group text-center bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 border border-orange-100 hover:shadow-xl transition-all duration-500">
                            <div className="relative mb-8">
                                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-lg">
                                    <Coffee className="h-10 w-10 text-white" />
                                </div>
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center">
                                    <Star className="h-4 w-4 text-white" />
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">True Cinnamon</h3>
                            <p className="text-gray-600 leading-relaxed">
                                The only authentic cinnamon species (Cinnamomum verum) recognized by botanists worldwide, native
                                exclusively to Sri Lanka.
                            </p>
                        </div>

                        <div className="group text-center bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100 hover:shadow-xl transition-all duration-500">
                            <div className="relative mb-8">
                                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-lg">
                                    <Heart className="h-10 w-10 text-white" />
                                </div>
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center">
                                    <CheckCircle className="h-4 w-4 text-white" />
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Ultra-Safe</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Contains less than 0.004% coumarin compared to cassia&apos;s 5%, making it completely safe for daily
                                consumption and therapeutic use.
                            </p>
                        </div>

                        <div className="group text-center bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100 hover:shadow-xl transition-all duration-500">
                            <div className="relative mb-8">
                                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-lg">
                                    <Zap className="h-10 w-10 text-white" />
                                </div>
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                                    <Star className="h-4 w-4 text-white" />
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Exquisite Flavor</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Delicate, sweet, and complex flavor profile with subtle citrus notes that enhance both sweet and savory
                                culinary creations.
                            </p>
                        </div>

                        <div className="group text-center bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100 hover:shadow-xl transition-all duration-500">
                            <div className="relative mb-8">
                                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-lg">
                                    <Award className="h-10 w-10 text-white" />
                                </div>
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-400 rounded-full flex items-center justify-center">
                                    <Globe className="h-4 w-4 text-white" />
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Global Recognition</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Preferred by world-renowned chefs, health experts, and discerning consumers who demand the finest
                                quality spices.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Cultivation Process */}
            <section className="py-32 bg-gradient-to-br from-gray-50 to-orange-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-20 items-center">
                        <div>
                            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 rounded-full px-6 py-3 mb-8 border border-orange-200">
                                <Mountain className="h-5 w-5" />
                                <span className="font-bold">From Garden to Global</span>
                            </div>

                            <h2 className="text-5xl font-black text-gray-900 mb-8 leading-tight">
                                Ancient Wisdom Meets
                                <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent"> Modern Excellence</span>
                            </h2>

                            <p className="text-xl text-gray-600 mb-12 leading-relaxed">
                                Our Ceylon Cinnamon journey begins in Sri Lanka&apos;s pristine central highlands, where generations of
                                skilled artisans have perfected the art of cinnamon cultivation.
                            </p>

                            <div className="space-y-8">
                                <div className="flex items-start space-x-6">
                                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center shadow-lg">
                                        <Mountain className="h-8 w-8 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3">Highland Terroir</h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            Grown at optimal altitudes of 500-2000 meters where the unique combination of tropical climate,
                                            rich soil, and monsoon rains creates the perfect environment for premium cinnamon trees.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-6">
                                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                                        <Users className="h-8 w-8 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3">Master Artisan Processing</h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            Hand-peeled by master craftsmen using traditional techniques passed down through centuries,
                                            ensuring each quill maintains its delicate structure and aromatic oils.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-6">
                                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg">
                                        <Shield className="h-8 w-8 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3">Quality Assurance</h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            Every batch undergoes rigorous testing for purity, moisture content, and essential oil
                                            concentration to ensure only the finest cinnamon reaches international markets.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="absolute -top-8 -left-8 w-32 h-32 bg-gradient-to-br from-orange-200 to-red-200 rounded-2xl transform rotate-12"></div>
                            <div className="relative bg-white rounded-2xl p-8 shadow-2xl border border-orange-100">
                                <img
                                    src="https://images.pexels.com/photos/4198933/pexels-photo-4198933.jpeg"
                                    alt="Traditional cinnamon processing in Sri Lanka"
                                    className="w-full h-80 object-cover rounded-xl mb-6"
                                />
                                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-6 shadow-xl text-white">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold mb-1">2000+</div>
                                        <div className="text-sm opacity-90">Years Heritage</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Health Benefits */}
            <section className="py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full px-8 py-4 mb-8 border border-green-200">
                            <Heart className="h-6 w-6" />
                            <span className="font-bold text-lg">Health &amp; Wellness</span>
                        </div>
                        <h2 className="text-5xl font-black text-gray-900 mb-8">
                            Nature&apos;s Most Powerful
                            <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"> Wellness Spice</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                            Ceylon Cinnamon isn&apos;t just a culinary delight—it&apos;s a scientifically proven wellness enhancer with
                            remarkable health benefits
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="group bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-red-100 relative overflow-hidden">
                            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                <Heart className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Cardiovascular Support</h3>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                Supports healthy cholesterol levels and promotes optimal blood circulation, contributing to overall
                                heart health and cardiovascular wellness.
                            </p>
                            <div className="flex items-center text-red-600 font-semibold">
                                <CheckCircle className="h-4 w-4 mr-2" />
                                <span>Clinically Studied</span>
                            </div>
                        </div>

                        <div className="group bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-blue-100 relative overflow-hidden">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                <Thermometer className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Blood Sugar Balance</h3>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                May help support healthy blood sugar levels and improve insulin sensitivity, making it beneficial for
                                metabolic health management.
                            </p>
                            <div className="flex items-center text-blue-600 font-semibold">
                                <CheckCircle className="h-4 w-4 mr-2" />
                                <span>Research Backed</span>
                            </div>
                        </div>

                        <div className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-green-100 relative overflow-hidden">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                <Shield className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Antioxidant Powerhouse</h3>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                Packed with powerful antioxidants that help protect cells from oxidative stress and support overall
                                health and longevity.
                            </p>
                            <div className="flex items-center text-green-600 font-semibold">
                                <CheckCircle className="h-4 w-4 mr-2" />
                                <span>High ORAC Value</span>
                            </div>
                        </div>

                        <div className="group bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-yellow-100 relative overflow-hidden">
                            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                <Zap className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Anti-Inflammatory</h3>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                Natural anti-inflammatory compounds help reduce inflammation markers and support joint health and
                                overall comfort.
                            </p>
                            <div className="flex items-center text-yellow-600 font-semibold">
                                <CheckCircle className="h-4 w-4 mr-2" />
                                <span>Natural Compounds</span>
                            </div>
                        </div>

                        <div className="group bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-purple-100 relative overflow-hidden">
                            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                <Target className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Cognitive Support</h3>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                May support brain health and cognitive function through neuroprotective compounds and enhanced blood
                                flow to the brain.
                            </p>
                            <div className="flex items-center text-purple-600 font-semibold">
                                <CheckCircle className="h-4 w-4 mr-2" />
                                <span>Neuroprotective</span>
                            </div>
                        </div>

                        <div className="group bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-teal-100 relative overflow-hidden">
                            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                <CheckCircle className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Safe Daily Use</h3>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                Unlike cassia cinnamon, Ceylon Cinnamon&apos;s ultra-low coumarin content makes it completely safe for
                                daily consumption and therapeutic doses.
                            </p>
                            <div className="flex items-center text-teal-600 font-semibold">
                                <CheckCircle className="h-4 w-4 mr-2" />
                                <span>FDA Recognized</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-32 bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
                </div>

                <div className="max-w-5xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative">
                    <div className="inline-flex items-center space-x-3 bg-white/20 backdrop-blur-xl rounded-full px-8 py-4 mb-10 border border-white/30">
                        <Coffee className="h-6 w-6 text-white" />
                        <span className="text-white font-bold text-lg">Ready to Experience?</span>
                    </div>

                    <h2 className="text-5xl md:text-6xl font-black text-white mb-8 leading-tight">
                        Experience the <span className="text-yellow-300"> True Cinnamon Difference</span>
                    </h2>

                    <p className="text-xl text-white/90 mb-16 leading-relaxed max-w-4xl mx-auto">
                        Join Australia&apos;s leading importers and health-conscious consumers who choose authentic Ceylon Cinnamon
                        for its unmatched quality, safety, and health benefits.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                        <Link
                            to="/#contact"
                            className="group bg-white hover:bg-gray-50 text-orange-600 px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl flex items-center space-x-3"
                        >
                            <span>Get Wholesale Pricing</span>
                            <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" />
                        </Link>
                        <a
                            href="mailto:exports@claudesspecial.lk"
                            className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/50 hover:border-white/80 px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 backdrop-blur-xl"
                        >
                            Email Direct
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="flex items-center justify-center space-x-3 mb-6">
                            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                                <Coffee className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-white">Claude&apos;s Special</div>
                                <div className="text-orange-400 font-medium">Authentic Ceylon Cinnamon</div>
                            </div>
                        </div>
                        <p className="text-gray-300 mb-6 max-w-3xl mx-auto">
                            The world&apos;s finest Ceylon Cinnamon, sourced directly from Sri Lanka&apos;s pristine highlands and
                            delivered to Australia with uncompromising quality and authenticity.
                        </p>
                        <div className="flex justify-center space-x-8 text-gray-300 mb-8">
                            <span className="flex items-center space-x-2">
                                <Coffee className="h-4 w-4 text-orange-400" />
                                <span>Premium Quality</span>
                            </span>
                            <span className="flex items-center space-x-2">
                                <Shield className="h-4 w-4 text-green-400" />
                                <span>Safe Daily Use</span>
                            </span>
                            <span className="flex items-center space-x-2">
                                <Globe className="h-4 w-4 text-blue-400" />
                                <span>Global Export</span>
                            </span>
                        </div>
                        <p className="text-gray-500">
                            &copy; 2025 Claude&apos;s Special. All rights reserved. | Authentic Ceylon Cinnamon Exports
                        </p>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default CinnamonPage;
